import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTranslation } from '../app/LanguageContext';
import { getMyProfile } from '../app/api';
import { getToken } from '../app/auth';
import UserMenu from '../components/UserMenu';
import WorkspaceSelector from '../components/WorkspaceSelector';
import { useWorkspace } from '../app/WorkspaceContext';

const drawerWidth = 240;
const SIDEBAR_STORAGE_KEY = 'appLayout.sidebarOpen';

const AppLayout = ({ children }) => {
  const { translate } = useTranslation();
  const { workspaces, loading } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(() => {
    try {
      const v = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      if (v === null) return true;
      return JSON.parse(v) === true;
    } catch {
      return true;
    }
  });
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (user && !loading && workspaces.length === 0 && location.pathname !== '/workspaces/new') {
      navigate('/workspaces/new', { replace: true });
    }
  }, [user, loading, workspaces.length, location.pathname, navigate]);

  useEffect(() => {
    const loadUser = async () => {
      const token = getToken();
      if (!token) {
        return;
      }
      try {
        const response = await getMyProfile();
        if (response.data) {
          setUser(response.data);
        }
      } catch (error) {
        console.error('Failed to load user profile:', error);
      }
    };
    loadUser();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(mobileOpen));
    } catch (_) {}
  }, [mobileOpen]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const showMenu = workspaces.length > 0 || loading;

  const menuItems = [
    { text: translate('common.home'), icon: <HomeIcon />, path: '/' },
    { text: translate('common.users'), icon: <PeopleIcon />, path: '/users' },
    { text: translate('common.customers'), icon: <PersonIcon />, path: '/customers' },
    { text: translate('common.products'), icon: <ShoppingCartIcon />, path: '/products' },
    { text: translate('common.orders'), icon: <ReceiptIcon />, path: '/orders' },
    { text: translate('common.medias'), icon: <PhotoLibraryIcon />, path: '/medias' },
    { text: translate('common.settings'), icon: <SettingsIcon />, path: '/settings' },
  ];


  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          {showMenu && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Eagle Admin Panel
          </Typography>
          {user && (
            <>
              {showMenu && <WorkspaceSelector />}
              <UserMenu user={user} />
            </>
          )}
        </Toolbar>
      </AppBar>
      {showMenu && (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        <Box>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton onClick={() => navigate(item.path)}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
      )}
      {showMenu && mobileOpen && (
        <Drawer
          variant="persistent"
          open={mobileOpen}
          sx={{
            display: { xs: 'none', sm: 'block' },
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              top: 64,
            },
          }}
        >
          <Box>
            <List>
              {menuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <ListItemButton onClick={() => navigate(item.path)}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Drawer>
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: showMenu && mobileOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { sm: showMenu && mobileOpen ? `${drawerWidth}px` : 0 },
          height: '100vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          transition: (theme) =>
            theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default AppLayout;


